import {UserApi, i18nApi} from '@core/api'
import useJwt from '@/auth/jwt/useJwt'
import {i18n, loadLanguageAsync} from '@/libs/i18n'
import Ability from '@/libs/acl/ability';
import Vue from 'vue';
import {createCounterListener} from "@/libs/counter";

let counterListener;

const DECLARATION_APPROVALS_STORAGE_KEY = 'declarationApprovalsByOrganization';

const getOrganizationStorageKey = organizationId => (
    organizationId === null || typeof organizationId === 'undefined' || organizationId === ''
        ? null
        : String(organizationId)
);

const readDeclarationApprovals = () => {
    const stored = localStorage.getItem(DECLARATION_APPROVALS_STORAGE_KEY);

    // The previous value was global and cannot safely be assigned to the
    // currently selected organization.
    localStorage.removeItem('dApproved');

    if (!stored) {
        return {};
    }

    try {
        const approvals = JSON.parse(stored);

        if (!approvals || typeof approvals !== 'object' || Array.isArray(approvals)) {
            return {};
        }

        return Object.keys(approvals)
            .filter(organizationId => approvals[organizationId] === true)
            .reduce((result, organizationId) => ({
                ...result,
                [organizationId]: true,
            }), {});
    } catch (error) {
        return {};
    }
};

const persistDeclarationApprovals = approvals => {
    localStorage.setItem(DECLARATION_APPROVALS_STORAGE_KEY, JSON.stringify(approvals));
};

export default {
    state() {
        let profile = localStorage.getItem('profile');
        let organization = localStorage.getItem('organization');
        let organizations = localStorage.getItem('organizations');
        let runtimeConfig = localStorage.getItem('rc');
        let swiftLogin = localStorage.getItem('sl');
        let ability = localStorage.getItem('ability');
        ability = ability ? JSON.parse(ability) : [];

        if (ability.length === 0) {
            ability.push({action: 'read', subject: 'Auth'})
        }

        profile = profile ? JSON.parse(profile) : {
            attributes: {
                subscriber: {id: null, title: null},
                subscription: {
                    isActive: false,
                },
                productRequest: {
                    activeReceivedRequestsCount: 0,
                    activeSentRequestsCount: 0,
                },
                system: {env: 'undefined'}
            },
            settings: {}
        };
        let productRequest = profile.attributes ? profile.attributes.productRequest : null;

        return {
            swiftLogin: swiftLogin ? swiftLogin === '1' : false,
            activeReceivedRequestsCount: productRequest ? productRequest.activeReceivedRequestsCount : 0,
            activeSentRequestsCount: productRequest ? productRequest.activeSentRequestsCount : 0,
            profile: profile,
            rootOrganizationState: true,
            organization: organization ? JSON.parse(organization) : {},
            organizations: organizations ? JSON.parse(organizations) : [],
            ability: ability,
            language: i18n.locale,
            declarationApprovalsByOrganization: readDeclarationApprovals(),
            runtimeConfig: runtimeConfig ? JSON.parse(runtimeConfig) : {},
            isFreeSubscriber: null
        };
    },
    getters: {
        env(state) {
            return state.profile.attributes.system.env;
        },
        isDeclarationApproved(state) {
            const organizationId = getOrganizationStorageKey(state.organization.id);

            return organizationId !== null
                && state.declarationApprovalsByOrganization[organizationId] === true;
        },
        isDeclarationApprovedForOrganization: state => organizationId => {
            const key = getOrganizationStorageKey(organizationId);

            return key !== null && state.declarationApprovalsByOrganization[key] === true;
        },
        activeReceivedRequestsCount(state) {
            return state.activeReceivedRequestsCount;
        },
        activeSentRequestsCount(state) {
            return state.activeSentRequestsCount;
        },
        profile(state) {
            return state.profile;
        },
        sendEmailPreferences(state) {
            return state.profile.settings.sendEmailPreferences;
        },
        showSendEmailSettingsStep(state) {
            //  either organization or profile settings should be shown
            return state.organization.settings.requestSettings.showSendEmailSettingsStep || state.profile.settings.showSendEmailSettingsStep;
        },
        subscriber(state) {
            return state.profile.attributes.subscriber;
        },
        subscription(state, getters) {
            return getters.profile.attributes.subscription;
        },
        rootOrganizationState(state) {
            return state.rootOrganizationState;
        },
        organization(state) {
            return state.organization;
        },
        organizations(state) {
            return state.organizations;
        },
        language(state) {
            return state.language;
        },
        ability(state) {
            return state.ability;
        },
        attributes(state) {
            return state.profile.attributes;
        },
        runtimeConfig(state) {
            return state.runtimeConfig;
        },
        isFreeSubscriber(state) {
            return state.isFreeSubscriber;
        },
        isFullMaterialDeclarationEnabled(state) {
            return state.organization.settings.requestSettings.replyProcessFlow === 'fmd';
        },
        isUnclassifiedVerificationEnabled(state) {
            const attributes = state.profile && state.profile.attributes ? state.profile.attributes : {};
            const productDocuments = attributes.productDocuments || {};

            return productDocuments.unclassifiedVerificationUploadEnabled === true;
        }
    },

    mutations: {
        declarationApproved(state, organizationId = state.organization.id) {
            const key = getOrganizationStorageKey(organizationId);

            if (key === null) {
                return;
            }

            Vue.set(state.declarationApprovalsByOrganization, key, true);
            persistDeclarationApprovals(state.declarationApprovalsByOrganization);
        },
        clearDeclarationApprovedState(state) {
            state.declarationApprovalsByOrganization = {};
            localStorage.removeItem(DECLARATION_APPROVALS_STORAGE_KEY);
            localStorage.removeItem('dApproved');
        },
        swiftLogin(state) {
            state.swiftLogin = true;
            localStorage.setItem('sl', '1');
        },
        setProfile(state, profile) {
            state.profile = Object.assign({}, state.profile, profile);
            if (profile.attributes) {
                if (profile.attributes.productRequest) {
                    state.activeReceivedRequestsCount = profile.attributes.productRequest.activeReceivedRequestsCount;
                    state.activeSentRequestsCount = profile.attributes.productRequest.activeSentRequestsCount;
                }
            }
            localStorage.setItem('profile', JSON.stringify(profile));
        },
        setOrganization(state, organization) {
            state.organization = organization;
            localStorage.setItem('organization', JSON.stringify(organization));
        },
        setOrganizations(state, organizations) {
            state.organizations = organizations;
            localStorage.setItem('organizations', JSON.stringify(organizations));
        },
        setAbility(state, ability) {
            state.ability = ability;

            if (null === ability || ability.length === 0) {
                localStorage.removeItem('ability');
            } else {
                Ability.update(ability);
                localStorage.setItem('ability', JSON.stringify(ability));
            }
        },
        setLanguage(state, language) {
            state.language = language;
        },
        updateSendEmailPreferences(state, preferences) {
            state.profile.settings.sendEmailPreferences = {
                ...preferences,
                transportConfiguration: preferences.configuration
            };
            localStorage.setItem('profile', JSON.stringify(state.profile));
        },
        doNotShowSendEmailSettingsStep(state) {
            state.profile.settings.showSendEmailSettingsStep = false;
            localStorage.setItem('profile', JSON.stringify(state.profile));
        },
        clearRuntimeConfig(state) {
            state.runtimeConfig = {};
            localStorage.setItem('rc', '{}');
            localStorage.setItem('rc_ar', '[]');
        },
        setRuntimeProperty(state, property) {
            state.runtimeConfig[property.name] = property.value;
            localStorage.setItem('rc', JSON.stringify(state.runtimeConfig));

            if (property.removeOnOrganizationSwitch) {
                const autoRemoveConfig = JSON.parse(localStorage.getItem('rc_ar') || '[]');
                if (!autoRemoveConfig.includes(property.name)) {
                    autoRemoveConfig.push(property.name);
                    localStorage.setItem('rc_ar', JSON.stringify(autoRemoveConfig));
                }
            }
        },
        updateShowSendEmailSettingsStepForOrganization(state, value) {
            state.organization.settings.requestSettings.showSendEmailSettingsStep = value;
            localStorage.setItem('organization', JSON.stringify(state.organization));
        },
        updateReplyProcessFlow(state, flow) {
            state.organization.settings.requestSettings.replyProcessFlow = flow;
            localStorage.setItem('organization', JSON.stringify(state.organization));
        },
        updateShowProductStatistics(state, value) {
            state.organization.settings.statisticSettings.showProductStatistics = value;
            localStorage.setItem('organization', JSON.stringify(state.organization));
        },
        updateRootOrganizationState(state, value) {
            state.rootOrganizationState = value;
        },
        autoRemoveRuntimeProperties(state) {
            //  auto remove runtime properties marked with removeOnOrganizationSwitch
            const autoRemoveConfig = JSON.parse(localStorage.getItem('rc_ar') || '[]');
            autoRemoveConfig.forEach((name) => {
                if (state.runtimeConfig.hasOwnProperty(name)) {
                    delete state.runtimeConfig[name];
                }
            });
            localStorage.setItem('rc', JSON.stringify(state.runtimeConfig));
            localStorage.setItem('rc_ar', '[]');
        },
        setCounters(state, counters) {
            Object.keys(counters).forEach(key => {
                state[key] = counters[key];
            });
        },
        markAsFreeSubscriber(state) {
            state.isFreeSubscriber = true;
        }
    },
    actions: {
        setLanguage({commit}, language) {
            return i18nApi.switchLanguage(language)
                .then(() => loadLanguageAsync(language))
                .then(() => {
                    commit('setLanguage', language);
                });
        },

        declarationApproved({commit}, organizationId) {
            commit('declarationApproved', organizationId);
        },

        swiftLoginSuccessful({commit}) {
            commit('clearDeclarationApprovedState');
            commit('swiftLogin');
        },

        accessTokenRefreshed({commit}) {
            commit('clearDeclarationApprovedState');
        },

        updateUserProfile({commit, dispatch, state}) {
            return UserApi
                .getProfile()
                .then(response => {
                    commit('setProfile', response);
                    commit('setAbility', response.abilities);
                    commit('autoRemoveRuntimeProperties');

                    if (response.attributes.organization) {
                        commit('setOrganization', response.attributes.organization);
                    }

                    if (response.settings.language !== state.language) {
                        return loadLanguageAsync(response.settings.language)
                            .then(() => {
                                commit('setLanguage', response.settings.language);
                                dispatch('initializeFreeSubscriber');
                                dispatch('configureMenu');
                                dispatch('loggedIn');
                            });
                    }

                    dispatch('initializeFreeSubscriber');
                    dispatch('configureMenu');
                    dispatch('loggedIn');
                });
        },

        setOrganization({commit, dispatch, state}, organization) {
            if (!state.organizations.find(o => o.id === organization.id)) {
                state.organizations.push(organization);
                commit('setOrganizations', state.organizations);
            }

            commit('setOrganization', organization)

            return dispatch('updateUserProfile');
        },

        selectOrganization({commit, dispatch, state}, organization) {
            return new Promise((resolve, reject) => {
                UserApi
                    .selectOrganization(organization.id)
                    .then(() => {
                        return dispatch('setOrganization', organization)
                            .then(resolve)
                            .catch(reject);
                    })
                    .catch(reject)
                ;
            });
        },

        updateShowSendEmailSettingsStepForOrganization({commit}, value) {
            commit('updateShowSendEmailSettingsStepForOrganization', value);
        },

        updateReplyProcessFlow({commit}, flow) {
            commit('updateReplyProcessFlow', flow);
        },

        updateShowProductStatistics({commit}, value) {
            commit('updateShowProductStatistics', value);
        },

        rootOrganizationUpdated({commit, state}) {
            commit('updateRootOrganizationState', !state.rootOrganizationState);
        },

        updateSendEmailPreferences({commit}, preferences) {
            return new Promise((resolve, reject) => {
                UserApi
                    .updateEmailPreferences(preferences)
                    .then(() => {
                        commit('updateSendEmailPreferences', preferences);
                        resolve();
                    })
                    .catch(reject)
                ;
            });
        },

        doNotShowSendEmailSettingsStep({commit}) {
            return new Promise((resolve, reject) => {
                UserApi
                    .updateSettings({
                        showSendEmailSettingsStep: false
                    })
                    .then(() => {
                        commit('doNotShowSendEmailSettingsStep');
                        resolve();
                    })
                    .catch(reject)
                ;
            });
        },

        setRuntimeProperty({commit}, property) {
            commit('setRuntimeProperty', property);
        },

        initializeFreeSubscriber({commit, state}) {
            if (state.swiftLogin && false === state.profile.attributes.subscription.isActive) {
                commit('markAsFreeSubscriber');
            }
        },

        configureMenu({commit, state}) {
            if (state.isFreeSubscriber) {
                commit('verticalMenu/UPDATE_VERTICAL_MENU_COLLAPSED', true);
            }
        },

        refreshCounters() {
            if (counterListener) {
                counterListener.refresh();
            }
        },

        initialized({commit, dispatch}) {
            counterListener = createCounterListener((data) => {
                commit('setCounters', data);
            });
            counterListener.listen();

            dispatch('initializeFreeSubscriber')
            dispatch('configureMenu');
        },

        loggedIn() {
            counterListener.listen();
        },

        logout({commit, dispatch}) {
            localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
            localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)
            localStorage.removeItem('sl');

            counterListener.stop();

            commit('setProfile', {})
            commit('setOrganization', {})
            commit('setOrganizations', [])
            commit('setAbility', null)
            commit('clearDeclarationApprovedState')
            commit('clearRuntimeConfig')
        }
    }
};
